/** Transient confirmations and failures.
 *
 * Hand-rolled rather than another dependency: this is a list, a timeout and a
 * portal-free fixed container, and the app already carries enough libraries.
 */
import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import { cn } from '../../lib/utils'

type Variant = 'default' | 'success' | 'critical'

interface Toast {
  id: number
  title: string
  description?: string
  variant: Variant
}

interface ToastContextValue {
  toast: (input: { title: string; description?: string; variant?: Variant }) => void
  dismiss: (id: number) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const DURATION_MS = 5000

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const nextId = useRef(1)
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>())

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((item) => item.id !== id))
    const timer = timers.current.get(id)
    if (timer) {
      clearTimeout(timer)
      timers.current.delete(id)
    }
  }, [])

  const toast = useCallback<ToastContextValue['toast']>(
    ({ title, description, variant = 'default' }) => {
      const id = nextId.current++
      setToasts((current) => [...current, { id, title, description, variant }])
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), DURATION_MS),
      )
    },
    [dismiss],
  )

  // Clearing on unmount stops a timeout firing setState against a dead tree.
  useEffect(() => {
    const pending = timers.current
    return () => {
      pending.forEach(clearTimeout)
      pending.clear()
    }
  }, [])

  const value = useMemo(() => ({ toast, dismiss }), [toast, dismiss])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toaster toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext)
  if (!context) throw new Error('useToast must be used inside a ToastProvider')
  return context
}

const ICONS: Record<Variant, typeof Info> = {
  default: Info,
  success: CheckCircle2,
  critical: AlertTriangle,
}

const ACCENTS: Record<Variant, string> = {
  default: 'text-info',
  success: 'text-success',
  critical: 'text-critical',
}

function Toaster({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: number) => void }) {
  if (toasts.length === 0) return null

  return (
    <div
      className="pointer-events-none fixed bottom-0 right-0 z-[60] flex w-full max-w-sm flex-col gap-2 p-4"
      aria-live="polite"
    >
      {toasts.map((item) => {
        const Icon = ICONS[item.variant]
        return (
          <div
            key={item.id}
            role={item.variant === 'critical' ? 'alert' : 'status'}
            className={cn(
              'pointer-events-auto flex items-start gap-2.5 rounded-lg border border-border bg-popover p-3 shadow-popover',
              'animate-in slide-in-from-bottom-2 fade-in-0',
            )}
          >
            <Icon className={cn('mt-0.5 size-4 shrink-0', ACCENTS[item.variant])} aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">{item.title}</p>
              {item.description && (
                <p className="mt-0.5 text-xs text-muted-foreground">{item.description}</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => onDismiss(item.id)}
              className="shrink-0 rounded-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              <X className="size-3.5" />
              <span className="sr-only">Dismiss</span>
            </button>
          </div>
        )
      })}
    </div>
  )
}
